// Copyright ©️ 2026 eVolve MEP, LLC

import { useMemo } from 'react';

import type { BendTypeInput } from '@evolvemep/api-ts';
import { Text } from '@mantine/core';

import { convertInchesToImperialString } from 'helpers/convertInchesToImperialString';
import { isAlmostExactlyEqual } from 'helpers/isAlmostExactlyEqual';
import { isNil, isNotNil } from 'helpers/isNotNil';
import { useBendCreatorFormContext } from 'modules/Shop/EvolveFabrication/BendCreator/FormComponents/useBendCreatorFormContext';
import { useBendCreatorValidationContext } from 'modules/Shop/EvolveFabrication/BendCreator/FormComponents/useBendCreatorValidationContext';

type Props = {
  bendTypeInput: BendTypeInput;
};

export const BendDimInputValidationMessage = ({ bendTypeInput: { keyName, isAngle } }: Props) => {
  const { values } = useBendCreatorFormContext();
  const { isValidating, lastValidation } = useBendCreatorValidationContext();

  const lastValidatedValue = useMemo(() => {
    if (isNil(lastValidation) || !lastValidation.isSuccess) return null;
    const { response } = lastValidation;
    return response.updatedBend.find(({ keyName: updatedKeyName }) => updatedKeyName === keyName)?.value ?? null;
  }, [lastValidation, keyName]);

  if (isValidating || isNil(lastValidation) || isNil(lastValidatedValue)) return null;

  // If the user has changed the value since the last validation, don't show the message
  const currentValue = values[keyName];
  if (currentValue !== lastValidatedValue) return null;

  // If the last validation request value is the same as the last validated value, don't show the message
  const lastRequestValue = lastValidation.request[keyName];
  if (isNotNil(lastRequestValue) && isAlmostExactlyEqual(lastRequestValue, lastValidatedValue)) return null;

  const formattedValue =
    lastRequestValue === null
      ? 'blank'
      : isAngle
        ? `${lastRequestValue.toLocaleString()}°`
        : convertInchesToImperialString(lastRequestValue * 12);

  return (
    <Text c="orange.8" fz="sm" mt={4}>
      Adjusted value from <b>{formattedValue}</b> due to bend validation.
    </Text>
  );
};
